import { useEffect, useRef } from 'react'
import { Alert, Col, DatePicker, Form, Input, Modal, Radio, Row, Select } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import {
  RECORDABLE_KINDS,
  fetchAdjustmentAccounts,
  fetchPaymentAccounts,
  recordAdvance,
  recordDebtRepayment,
  recordTxn,
  recordWithdrawal,
  type RecordableKind,
  type Stakeholder,
} from './stakeholderApi'

export type CapitalMode = 'record' | 'withdraw' | 'advance' | 'repay'

const PAYMENT_ACTIONS = { withdraw: recordWithdrawal, advance: recordAdvance, repay: recordDebtRepayment } as const

const TITLE: Readonly<Record<CapitalMode, string>> = {
  record: 'stakeholder.capital.recordTitle',
  withdraw: 'stakeholder.capital.withdrawTitle',
  advance: 'stakeholder.capital.advanceTitle',
  repay: 'stakeholder.capital.repayTitle',
}

const ACTION: Readonly<Record<CapitalMode, string>> = {
  record: 'stakeholder.capital.record',
  withdraw: 'stakeholder.capital.withdraw',
  advance: 'stakeholder.capital.advance',
  repay: 'stakeholder.capital.repay',
}

const NOTE: Readonly<Record<CapitalMode, string>> = {
  record: 'stakeholder.capital.recordNote',
  withdraw: 'stakeholder.capital.withdrawNote',
  advance: 'stakeholder.capital.advanceNote',
  repay: 'stakeholder.capital.repayNote',
}

interface CapitalTxnModalProps {
  stakeholder: Stakeholder | null
  mode: CapitalMode
  onClose: () => void
}

interface FormValues {
  kind: RecordableKind
  amount: Money | null
  txnDate: Dayjs
  accountId: string
  direction: 'INCREASE' | 'DECREASE'
  reference?: string
  note?: string
}

const text = (value: string | undefined): string | null => (value?.trim() ? value.trim() : null)

/** Kapital kiritish, tuzatish, kapitaldan olish, avans yoki qarzni qaytarish — har biri bitta moliya provodkasi. */
export function CapitalTxnModal({ stakeholder, mode, onClose }: CapitalTxnModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const key = useRef('')
  const kind = Form.useWatch('kind', form)
  const entered = Form.useWatch('amount', form)
  const adjusting = mode === 'record' && (kind === 'ADJUSTMENT' || kind === 'OTHER')

  useEffect(() => {
    if (!stakeholder) return
    key.current = newIdempotencyKey(`stakeholder-${mode}`)
    form.setFieldsValue({
      kind: stakeholder.summary.totalInvestment.amount > 0 ? 'CAPITAL_CONTRIBUTION' : 'INITIAL_INVESTMENT',
      txnDate: tenantToday(),
      direction: 'INCREASE',
      reference: '',
      note: '',
      ...(mode === 'repay' ? { amount: stakeholder.summary.debt } : {}),
    })
  }, [stakeholder, mode, form])

  const paymentAccounts = useQuery({
    queryKey: queryKeys.stakeholder.paymentAccounts,
    queryFn: ({ signal }) => fetchPaymentAccounts(signal),
    enabled: stakeholder !== null,
  })
  const adjustmentAccounts = useQuery({
    queryKey: queryKeys.stakeholder.adjustmentAccounts,
    queryFn: ({ signal }) => fetchAdjustmentAccounts(signal),
    enabled: stakeholder !== null && adjusting,
  })
  const accounts = adjusting ? adjustmentAccounts : paymentAccounts

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!stakeholder) throw new Error('stakeholder')
      const amountMinor = values.amount?.amount ?? 0
      const date = values.txnDate.format('YYYY-MM-DD')
      if (mode !== 'record') {
        await PAYMENT_ACTIONS[mode](
          stakeholder.id,
          { amountMinor, paidOn: date, accountId: values.accountId, reference: text(values.reference), note: text(values.note) },
          key.current,
        )
        return
      }
      await recordTxn(
        stakeholder.id,
        {
          kind: values.kind,
          amountMinor,
          txnDate: date,
          accountId: values.accountId,
          direction: adjusting ? values.direction : null,
          reference: text(values.reference),
          note: text(values.note),
        },
        key.current,
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const summary = stakeholder?.summary
  const debt = mode === 'withdraw' ? summary?.debt : undefined
  const payable = summary?.payable
  const payableAfter = payable && mode === 'advance' ? payable.amount - (entered?.amount ?? 0) : null
  const balanceLine = !summary
    ? undefined
    : mode === 'repay'
      ? t('stakeholder.capital.debtNow', { amount: formatMoneyValue(summary.debt) })
      : mode === 'advance' && payable && payableAfter !== null
        ? t('stakeholder.capital.advanceAfter', {
            before: formatMoneyValue(payable),
            after: formatMoneyValue(money(payableAfter, payable.currency)),
          })
        : t('stakeholder.capital.balance', { amount: formatMoneyValue(summary.capital) })

  return (
    <Modal
      open={stakeholder !== null}
      title={t(TITLE[mode])}
      okText={t(ACTION[mode])}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
      width={640}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t(NOTE[mode])}
        description={balanceLine}
      />
      {payable && payableAfter !== null && payableAfter < 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stakeholder.capital.advanceDebtWarn', {
            amount: formatMoneyValue(money(-payableAfter, payable.currency)),
          })}
        />
      ) : null}
      {debt && debt.amount > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stakeholder.capital.debtBlocks', { amount: formatMoneyValue(debt) })}
        />
      ) : null}
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Row gutter={16}>
          {mode === 'record' ? (
            <Col xs={24}>
              <Form.Item name="kind" label={t('stakeholder.capital.kind')}>
                <Select options={RECORDABLE_KINDS.map((value) => ({ value, label: t(`stakeholder.txnKind.${value}`) }))} />
              </Form.Item>
            </Col>
          ) : null}
          {adjusting ? (
            <Col xs={24}>
              <Form.Item name="direction" label={t('stakeholder.capital.direction')}>
                <Radio.Group
                  options={[
                    { value: 'INCREASE', label: t('stakeholder.capital.increase') },
                    { value: 'DECREASE', label: t('stakeholder.capital.decrease') },
                  ]}
                />
              </Form.Item>
            </Col>
          ) : null}
          <Col xs={24} md={12}>
            <Form.Item
              name="amount"
              label={t('stakeholder.capital.amount')}
              rules={[
                {
                  validator: (_, value: Money | null) =>
                    value && value.amount > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('stakeholder.validation.amount'))),
                },
              ]}
            >
              <MoneyInput minMinor={1} {...(mode === 'repay' && summary ? { maxMinor: summary.debt.amount } : {})} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="txnDate" label={t('stakeholder.capital.date')} rules={[{ required: true, message: t('stakeholder.validation.date') }]}>
              <DatePicker style={{ width: '100%' }} disabledDate={(day) => day.isAfter(tenantToday(), 'day')} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="accountId"
              label={adjusting ? t('stakeholder.capital.counterAccount') : t('stakeholder.capital.moneyAccount')}
              extra={adjusting ? t('stakeholder.capital.counterAccountHint') : t('stakeholder.capital.moneyAccountHint')}
              rules={[{ required: true, message: t('stakeholder.validation.account') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={accounts.isPending}
                options={(accounts.data ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${seededName('accounts', a.code, a.name, t)}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="reference" label={t('stakeholder.capital.reference')}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="note"
              label={t('stakeholder.capital.note')}
              rules={noteRules(mode, adjusting, t)}
            >
              <Input maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

function noteRules(mode: CapitalMode, adjusting: boolean, t: (key: string) => string) {
  if (adjusting) return [{ required: true, whitespace: true, message: t('stakeholder.validation.adjustmentNote') }]
  if (mode === 'advance') return [{ required: true, whitespace: true, message: t('stakeholder.validation.advanceNote') }]
  return []
}
